// League tier distribution and fixture row presentation.
import { cap } from '../../../lib/format';
import type { AdminFixtureRow, AdminTierCount } from '../../../lib/adminApi';
import { formatDateTime } from './format';
import { Pill } from './primitives';

/** Representative colour per tier index (bronze → elite), for the distribution bar. */
const TIER_HEX = ['#B87333', '#AEB4BF', '#E3A82B', '#8FB7C4', '#5AA6E6', '#7E5BD6'];
const tierHex = (tier: number): string => TIER_HEX[tier] ?? '#9CA3AF';

/** Stacked distribution of members across tiers, with a small per-tier legend. */
export function TierBar({ tiers }: { tiers: AdminTierCount[] }) {
  const total = tiers.reduce((n, t) => n + t.count, 0);
  if (total === 0) return <div className="text-[12px] text-ink3">No members yet</div>;
  const present = tiers.filter((t) => t.count > 0);
  return (
    <div>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-fill">
        {present.map((t) => (
          <div
            key={t.tier}
            style={{ width: `${(t.count / total) * 100}%`, backgroundColor: tierHex(t.tier) }}
            title={`${cap(t.tierName)}: ${t.count}`}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {present.map((t) => (
          <span key={t.tier} className="flex items-center gap-1 text-[11px] text-ink2">
            <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: tierHex(t.tier) }} />
            <span className="font-semibold">{cap(t.tierName)}</span>
            <span className="num text-ink3">{t.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** One fixture line: kickoff, teams, and result / live score / status. */
export function FixtureRow({ f }: { f: AdminFixtureRow }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 md:px-5">
      <div className="w-[96px] shrink-0 text-[11px] leading-tight text-ink3">{formatDateTime(f.kickoffAt)}</div>
      <div className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
        {f.homeTeam} <span className="text-ink3">v</span> {f.awayTeam}
      </div>
      <div className="shrink-0 text-right">
        {f.liveLabel ? (
          <Pill tone="danger">{f.liveLabel}</Pill>
        ) : f.resultLabel ? (
          <span className="num text-[13px] font-bold text-ink">{f.resultLabel}</span>
        ) : (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-ink3">{f.status}</span>
        )}
      </div>
    </div>
  );
}
